/** @jsx jsx */
import * as React from 'react';

import styled from '@emotion/styled';
import BookBadgeRenderer from 'app/components/badge/BookBadgeRenderer';

import { jsx } from '@emotion/core';
import { gray40, gray60, gray90 } from '@ridi/colors';
import { BadgeContainer } from 'app/components/book/PortraitBook';
import { Book } from '@ridi/web-ui';
import { BookDetailContext } from './BookDetailContext';
import { Icon } from '@ridi/rsg';
import { Popup, PopupHandle } from 'app/components/popup/Popup';
import { isAnimationContent, LinkedContent } from 'app/pages/bookDetail/types';
import { useLinkedContentLink } from 'app/pages/bookDetail/hooks';

//@ts-ignore
const FALLBACK_COVER_URL = global.Ridi?.globals?.staticUrl + '/books/dist/images/book_cover/cover_lazyload.png';

const LaftelCover = styled.img`
  width: 50px;
  height: auto;
`;

const LinkedContentThumbnailWrapper = styled.div`
  flex: 0 0 auto;
`;

const LinkedContentThumbnail = ({ content }: { content: LinkedContent }) => {
  const { isVerifiedAdult, adultBookCover } = React.useContext(BookDetailContext);
  const [ thumbnailUrl, setThumbnailUrl ] = React.useState((() => {
    if (isAnimationContent(content)) {
      return content.cover;
    }

    return (content.adults_only && !isVerifiedAdult) ?
      adultBookCover :
      (content.serial?.cover?.xxlarge || content.cover.xxlarge);
  })());

  const thumbnailErrorRef = React.useRef<(HTMLDivElement & HTMLImageElement) | null>(null);

  React.useEffect(() => {
    if (!thumbnailErrorRef.current) {
      return;
    }

    const onErrorHandler = () => setThumbnailUrl(FALLBACK_COVER_URL);
    thumbnailErrorRef.current.addEventListener('error', onErrorHandler, true);

    return () => {
      thumbnailErrorRef.current.removeEventListener('error', onErrorHandler);
    };
  }, []);

  if (isAnimationContent(content)) {
    return (
      <LinkedContentThumbnailWrapper>
        <LaftelCover ref={thumbnailErrorRef} src={thumbnailUrl} alt="애니메이션으로 보기" />
      </LinkedContentThumbnailWrapper>
    );
  }

  return (
    <LinkedContentThumbnailWrapper ref={thumbnailErrorRef}>
      <Book.Thumbnail
        thumbnailUrl={thumbnailUrl}
        thumbnailWidth={50}
        adultBadge={content.adults_only}
      >
        <BadgeContainer>
          <BookBadgeRenderer
            size={20}
            isWaitFree={!!content?.serial?.wait_for_free}
          />
        </BadgeContainer>
      </Book.Thumbnail>
    </LinkedContentThumbnailWrapper>
  );
};

const LinkedContentItemWrapper = styled.a`
  display: flex;
  align-items: center;
  min-height: 75px;
  margin: 8px 0;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const LinkedContentItemBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0 11px;
  width: 196px;
  box-sizing: border-box;
`;

const LinkedContentItemTitle = styled.span`
  color: ${gray40};
  font-size: 11px;
  font-weight: 500;
  line-height: 13px;
  letter-spacing: -0.06px;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LinkedContentItemText = styled.span`
  color: ${gray90};
  font-size: 14px;
  font-weight: 600;
  line-height: 15px;
  letter-spacing: -0.07px;
`;

const LinkedContentItemIcon = styled(Icon)`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  fill: ${gray60};
`;

const LinkedContentItem = ({ content, index }: { content: LinkedContent, index: number }) => {
  const title = isAnimationContent(content) ?
    content.title :
    content.serial?.title ?? content.title;

  const text = isAnimationContent(content) ?
    '애니메이션으로 보기' :
    content.extra_book_metadata.mapped_category.text;

  const { link, target, onClick } = useLinkedContentLink(content, index);

  return (
    <LinkedContentItemWrapper href={ link } onClick={ onClick } target={ target }>
      <LinkedContentThumbnail content={ content } />
      <LinkedContentItemBody>
        <LinkedContentItemTitle>{ title }</LinkedContentItemTitle>
        <LinkedContentItemText>{ text }</LinkedContentItemText>
      </LinkedContentItemBody>
      <LinkedContentItemIcon name="arrow_2_right" />
    </LinkedContentItemWrapper>
  );
};


const LinkedContentsPopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

interface LinkedContentsPopupProps {
  className: string,
  contents: LinkedContent[],
  children?: React.ReactNode
};

export const LinkedContentsPopup = React.forwardRef<PopupHandle, LinkedContentsPopupProps>(
  ({ className, contents }: LinkedContentsPopupProps, ref) => {
    return (
      <div className={ className }>
        <Popup title="" ref={ref}>
          <LinkedContentsPopupWrapper>
            {
              contents.map((content, index) => (
                <LinkedContentItem
                  content={content}
                  index={index}
                  key={isAnimationContent(content) ? `laftel-${content.laftel_id}` : `book-${content.book_id}`}
                />
              ))
            }
          </LinkedContentsPopupWrapper>
        </Popup>
      </div>
    )
  }
);



// WEBPACK FOOTER //
// ./pages/bookDetail/components/LinkedContentsPopup.tsx